import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartApi } from '../lib/api';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [cartItems, setCartItems] = useState(() => {
    // Load from localStorage for guests
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch cart from API when authenticated
  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setIsLoading(true);
    try {
      const items = await cartApi.get();
      // Transform API response to match our cart structure
      const transformedItems = items.map(item => ({
        ...item.product,
        cartItemId: item.cart_item_id,
        selectedSize: item.selected_size,
        selectedColor: item.selected_color,
        quantity: item.quantity
      }));
      setCartItems(transformedItems);
      localStorage.setItem('cart', JSON.stringify(transformedItems));
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchCart();
    }
  }, [authLoading, isAuthenticated, fetchCart]);

  // Save to localStorage when not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem('cart', JSON.stringify(cartItems));
    }
  }, [cartItems, isAuthenticated]);

  const addToCart = async (product, selectedSize, selectedColor, quantity = 1) => {
    if (isAuthenticated) {
      try {
        const response = await cartApi.add({
          product_id: product.id,
          selected_size: selectedSize,
          selected_color: selectedColor,
          quantity
        });
        
        // Refresh cart from server
        await fetchCart();
      } catch (error) {
        console.error('Error adding to cart:', error);
        throw error;
      }
    } else {
      // Guest cart - use localStorage
      setCartItems(prev => {
        const existingItem = prev.find(
          item => item.id === product.id && 
                  item.selectedSize === selectedSize && 
                  item.selectedColor === selectedColor
        );

        if (existingItem) {
          return prev.map(item =>
            item.id === product.id && 
            item.selectedSize === selectedSize && 
            item.selectedColor === selectedColor
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        }

        return [...prev, {
          ...product,
          selectedSize,
          selectedColor,
          quantity,
          cartItemId: `${product.id}_${selectedSize}_${selectedColor}_${Date.now()}`
        }];
      });
    }
  };

  const removeFromCart = async (cartItemId) => {
    if (isAuthenticated) {
      try {
        await cartApi.remove(cartItemId);
        await fetchCart();
      } catch (error) {
        console.error('Error removing from cart:', error);
      }
    } else {
      setCartItems(prev => prev.filter(item => item.cartItemId !== cartItemId));
    }
  };

  const updateQuantity = async (cartItemId, quantity) => {
    if (quantity <= 0) {
      await removeFromCart(cartItemId);
      return;
    }

    if (isAuthenticated) {
      try {
        await cartApi.update(cartItemId, quantity);
        await fetchCart();
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    } else {
      setCartItems(prev =>
        prev.map(item =>
          item.cartItemId === cartItemId ? { ...item, quantity } : item
        )
      );
    }
  };

  const clearCart = async () => {
    if (isAuthenticated) {
      try {
        await cartApi.clear();
        setCartItems([]);
        localStorage.removeItem('cart');
      } catch (error) {
        console.error('Error clearing cart:', error);
      }
    } else {
      setCartItems([]);
      localStorage.removeItem('cart');
    }
  };

  const cartTotal = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  const cartCount = cartItems.reduce(
    (count, item) => count + item.quantity,
    0
  );

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      cartTotal,
      cartCount,
      isCartOpen,
      setIsCartOpen,
      isLoading,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};
